// src/pages/charts/MiniLineChartMonth.jsx
import useIsMobile from '../../utils/windows';
import styles from '../widgets/Widget.module.css';

function formatBytesLabel(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = bytes;
    let i = 0;

    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }

    const value = v >= 100 ? Math.round(v) : v.toFixed(1);
    return `${value} ${units[i]}`; // ⬅️ 단위까지 표기
}

export default function MiniLineChartMonth({
    series = [],
    year,
    month,
    // 논리 좌표계 크기 (viewBox)
    vw = 520,
    vh = 220,
    padL = 80,
    padR = 16,
    padT = 10,
    padB = 30,
    yTicks = 4,
    xTicks = 6,
    yMinFixed = 0,
}) {
    const isMobile = useIsMobile();

    const AXIS_FONT_SIZE = isMobile ? 20 : 13; // 공통 기본 폰트 크기
    const X_LABEL_FONT_SIZE = AXIS_FONT_SIZE;
    const Y_LABEL_FONT_SIZE = AXIS_FONT_SIZE;
    const Y_LABEL_PADDING = AXIS_FONT_SIZE;

    // ── 유틸 함수 ──────────────────────────────
    const getDaysInMonth = (y, m) => (y && m ? new Date(y, m, 0).getDate() : 31);
    const daysInMonth = getDaysInMonth(year, month);
    const innerW = Math.max(0, vw - padL - padR);
    const innerH = Math.max(0, vh - padT - padB);
    const sortByDay = (arr) => [...arr].filter(Boolean).sort((a, b) => a[0] - b[0]);

    // ── y 범위 계산 ─────────────────────────────
    const allY = series
        .flatMap((s) => s.points ?? [])
        .map((p) => p?.[1])
        .filter((v) => Number.isFinite(v));
    const yMinRaw = allY.length ? Math.min(...allY) : 0;
    const yMaxRaw = allY.length ? Math.max(...allY) : 1;
    const yMin = yMinFixed ?? yMinRaw;
    const yMax = Math.max(yMaxRaw, yMin + 1);
    const span = yMax - yMin;

    const scaleX = (d) => padL + ((d - 1) / Math.max(daysInMonth - 1, 1)) * innerW;
    const scaleY = (v) => padT + innerH - ((v - yMin) / span) * innerH;
    const toPts = (pairs) =>
        !pairs?.length
            ? ''
            : sortByDay(pairs)
                  .map(([d, v]) => `${scaleX(d)},${scaleY(v)}`)
                  .join(' ');

    const xLabels = Array.from({ length: xTicks + 1 }, (_, i) => Math.round(1 + (daysInMonth - 1) * (i / xTicks)));
    const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => yMin + (span * i) / yTicks);

    const axisY = padT + innerH; // x축 y 위치

    // ── 포인트 마커 렌더 ────────────────────────
    const renderMarkers = (points, shape = 'circle', color = '#7efcff', markerSize = 5) =>
        sortByDay(points).map(([d, v], idx) => {
            const x = scaleX(d);
            const y = scaleY(v);
            const size = markerSize;
            const common = { className: styles.miniChart__marker, style: { fill: color, stroke: '#fff' } };

            if (shape === 'triangle') {
                const pts = `${x},${y - size} ${x - size},${y + size} ${x + size},${y + size}`;
                return <polygon key={idx} points={pts} {...common} />;
            } else if (shape === 'square') {
                return <rect key={idx} x={x - size / 2 - 1} y={y - size / 2 - 1} width={size + 2} height={size + 2} rx={1.5} {...common} />;
            } else if (shape === 'circle') {
                return <circle key={idx} cx={x} cy={y} r={size * 0.7} {...common} />;
            }
            return null;
        });

    // ── 렌더 ────────────────────────────────────
    return (
        <div className={styles.miniChartWrap}>
            {/* 부모 크기에 맞게 자동 스케일: viewBox만 고정 */}
            <svg viewBox={`0 0 ${vw} ${vh}`} className={styles.miniChart} aria-hidden>
                <rect x="0" y="0" width={vw} height={vh} rx="16" className={styles.miniChart__bg} />

                {/* 축 & 그리드 */}
                <g className={styles.miniChart__axes}>
                    {yLabels.map((yv, i) => {
                        const y = scaleY(yv);
                        return (
                            <g key={`yh-${i}`}>
                                <line x1={padL} y1={y} x2={padL + innerW} y2={y} className={styles.miniChart__grid} />
                                <text
                                    x={padL - 8}
                                    y={y}
                                    className={`${styles.miniChart__label} ${styles.miniChart__labelY}`}
                                    textAnchor="end"
                                    dominantBaseline="middle"
                                    style={{ fontSize: Y_LABEL_FONT_SIZE }} // ⬅️ 여기서 확실하게 키움
                                >
                                    {formatBytesLabel(yv)}
                                </text>
                            </g>
                        );
                    })}

                    {xLabels.map((d, i) => {
                        const x = scaleX(d);
                        return (
                            <g key={`x-${i}`}>
                                <line x1={x} y1={axisY} x2={x} y2={axisY + 4} className={styles.miniChart__tick} />
                                <text
                                    x={x}
                                    y={axisY + Y_LABEL_PADDING}
                                    className={`${styles.miniChart__label} ${styles.miniChart__labelX}`}
                                    textAnchor="middle"
                                    style={{ fontSize: X_LABEL_FONT_SIZE }}
                                >
                                    {d}
                                </text>
                            </g>
                        );
                    })}

                    <line x1={padL} y1={padT} x2={padL} y2={axisY} className={styles.miniChart__axis} />
                    <line x1={padL} y1={axisY} x2={padL + innerW} y2={axisY} className={styles.miniChart__axis} />
                </g>

                {/* 시리즈별 라인 & 포인트 */}
                {series.map((s, idx) => {
                    const stroke = s.color || '#7efcff';
                    const strokeWidth = (s.strokeWidth ?? isMobile) ? 3 : 2;
                    const markerSize = (s.markerSize ?? isMobile) ? 8 : 5;
                    const pts = toPts(s.points);
                    if (!pts) return null;

                    return (
                        <g key={idx}>
                            <polyline points={pts} className={styles.miniChart__line} style={{ stroke, strokeWidth }} fill="none" />
                            {s.shape && renderMarkers(s.points, s.shape, stroke, markerSize)}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}
